class InventoryChecker {
  constructor() {
    this.inventoryData = null;
    this.isTodaySelected = false;
    this.selectedLocationId = null;
    this.isLoading = false;
    this._cartSnapshot = null;
    this._cartSnapshotAt = 0;
    this._cartSnapshotPromise = null;
    this._updateCardsTimer = null;
    this._refreshTimer = null;
    this.API_URL = window.EveryTable?.api?.baseUrl || "https://admin-app.everytable-sh.com/api";
    this.init();
  }

  init() {
    
    // Check if today's date is selected
    this.checkIfTodaySelected();
    
    // Listen for date changes
    this.listenForDateChanges();
    
    // Listen for location changes
    this.listenForLocationChanges();
    this.listenForRenderChanges();
    
    // Initial check
    this.updateInventoryDisplay();
    
    // Add manual refresh button if needed
    // this.addRefreshButton();
  }

  queueRefresh(delay = 120) {
    if (this._refreshTimer) {
      clearTimeout(this._refreshTimer);
    }
    this._refreshTimer = setTimeout(() => {
      this.updateInventoryDisplay();
    }, delay);
  }

  checkIfTodaySelected() {
    const fulfillmentConstants = window.FulfillmentConstants;
    const dateKey = fulfillmentConstants && fulfillmentConstants.DATE ? fulfillmentConstants.DATE : 'selected_fulfillment_date';
    const storageDate = localStorage.getItem(dateKey);
    this.isTodaySelected = !!(storageDate && window.FulfillmentDateUtils?.isSelectedDateToday?.(storageDate));
  }

  normalizeLocationId(locationId) {
    if (window.EtInventoryDomain && typeof window.EtInventoryDomain.normalizeLocationId === 'function') {
      return window.EtInventoryDomain.normalizeLocationId(locationId);
    }
    const raw = (locationId || '').toString().trim();
    if (!raw) return '';
    if (raw.includes('gid://shopify/Location/')) {
      return raw.split('/').pop() || raw;
    }
    return raw;
  }

  normalizeProductId(productId) {
    if (window.EtInventoryDomain && typeof window.EtInventoryDomain.normalizeProductId === 'function') {
      return window.EtInventoryDomain.normalizeProductId(productId);
    }
    const raw = (productId || '').toString().trim();
    if (!raw) return '';
    if (raw.includes('gid://shopify/Product/')) {
      return raw.split('/').pop() || raw;
    }
    return raw;
  }

  isFulfillmentReadyForReactiveUpdates() {
    const isCommitted = !!(window.FulfillmentState && typeof window.FulfillmentState.isCommitted === 'function' && window.FulfillmentState.isCommitted());
    const isDraftMode = !!(window.FulfillmentState && typeof window.FulfillmentState.isDraftMode === 'function' && window.FulfillmentState.isDraftMode());
    return isCommitted && !isDraftMode;
  }

  listenForDateChanges() {
    const onFulfillmentDateChanged = () => {
      this.checkIfTodaySelected();
      this.updateInventoryDisplay();
    };

    // Listen for the fulfillment date changed event
    window.addEventListener('fulfillmentDateChanged', onFulfillmentDateChanged);
    document.addEventListener('fulfillmentDateChanged', onFulfillmentDateChanged);
    window.addEventListener('fulfillmentCommitted', onFulfillmentDateChanged);

    // Also listen for direct date selector changes
    const dateSelector = document.getElementById('fulfillment-date-delivery');
    if (dateSelector) {
      dateSelector.addEventListener('change', () => {
        setTimeout(() => {
          this.checkIfTodaySelected();
          this.updateInventoryDisplay();
        }, 100);
      });
    }
  }

  listenForLocationChanges() {
    const onLocationChanged = () => {
      const fulfillmentConstants = window.FulfillmentConstants;
      const locationKey = fulfillmentConstants && fulfillmentConstants.LOCATION_ID ? fulfillmentConstants.LOCATION_ID : 'selected_location_id';
      this.selectedLocationId = this.normalizeLocationId(localStorage.getItem(locationKey));
      this.updateInventoryDisplay();
    };

    // Listen for location changes in localStorage
    window.addEventListener('storage', (e) => {
      const fulfillmentConstants = window.FulfillmentConstants;
      const locationKey = fulfillmentConstants && fulfillmentConstants.LOCATION_ID ? fulfillmentConstants.LOCATION_ID : 'selected_location_id';
      if (e.key === locationKey) {
        this.selectedLocationId = this.normalizeLocationId(e.newValue);
        this.updateInventoryDisplay();
      }
    });

    // Also check for location changes on page load and after location selection
    const constants = window.FulfillmentConstants;
    const initialLocationKey = constants && constants.LOCATION_ID ? constants.LOCATION_ID : 'selected_location_id';
    this.selectedLocationId = this.normalizeLocationId(localStorage.getItem(initialLocationKey));
    
    // Listen for custom location change events
    window.addEventListener('locationChanged', onLocationChanged);
    document.addEventListener('locationChanged', onLocationChanged);
  }

  listenForRenderChanges() {
    const onRenderChange = () => {
      this.queueRefresh(120);
    };

    window.addEventListener('locationPricesReady', onRenderChange);
    window.addEventListener('variantFilterComplete', onRenderChange);
    document.addEventListener('catalogId:updated', onRenderChange);

    // Subscription page: sidebar cart is separate from Shopify cart; use it for sold-out on recharge cards
    let lastSidebarCartEventId = 0;
    const onSidebarCartUpdated = (e) => {
      if (!this.isFulfillmentReadyForReactiveUpdates()) return;
      const eventId = (e && e.timeStamp !== undefined && e.timeStamp !== null) ? e.timeStamp : Date.now();
      if (eventId === lastSidebarCartEventId) return;
      lastSidebarCartEventId = eventId;
      const sidebarCart = e && e.detail && e.detail.cart;
      if (!Array.isArray(sidebarCart)) return;
      const cartOverride = {
        items: sidebarCart.map(function (item) {
          return { variant_id: item.variantId, quantity: item.qty || 0 };
        })
      };
      this.updateProductCards(cartOverride);
    };
    window.addEventListener('sidebarCartUpdated', onSidebarCartUpdated);
    document.addEventListener('sidebarCartUpdated', onSidebarCartUpdated);

    window.addEventListener('pageshow', (event) => {
      const cacheWasCleared = !!window.__ET_INVENTORY_CACHE_CLEARED_AT;
      if (!event.persisted && !cacheWasCleared) return;
      this.inventoryData = null;
      this._cartSnapshot = null;
      this._cartSnapshotAt = 0;
      this.queueRefresh(0);
    });
  }

  async getLocationIdFromLocalStorage() {
    const fulfillmentConstants = window.FulfillmentConstants;
    const locationKey = fulfillmentConstants && fulfillmentConstants.LOCATION_ID ? fulfillmentConstants.LOCATION_ID : 'selected_location_id';
    return this.normalizeLocationId(localStorage.getItem(locationKey));
  }

  async fetchInventoryData() {
    if (!this.selectedLocationId) {
      return null;
    }

    if (this.isLoading) {
      return this.inventoryData;
    }

    this.isLoading = true;
    try {
      const locationIdToSearch = this.normalizeLocationId(this.selectedLocationId);
      let data = null;
      if (window.EtInventoryDomain && typeof window.EtInventoryDomain.fetchInventoryForLocation === 'function') {
        data = await window.EtInventoryDomain.fetchInventoryForLocation(locationIdToSearch);
      } else {
        const cacheKey = `inventory_data_${locationIdToSearch}`;
        const cachedData = sessionStorage.getItem(cacheKey);
        if (cachedData) {
          try {
            const parsed = JSON.parse(cachedData);
            if (parsed.timestamp && (Date.now() - parsed.timestamp) < 15 * 60 * 1000) {
              this.isLoading = false;
              return parsed.data;
            }
          } catch (e) {}
        }
        const response = await fetch(`${this.API_URL}/location/${locationIdToSearch}/inventory`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        data = await response.json();
        const cacheData = { data: data, timestamp: Date.now(), locationId: locationIdToSearch };
        sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
      }
      this.isLoading = false;
      return data;
    } catch (error) {
      console.error('Error fetching inventory data:', error);
      this.isLoading = false;
      return null;
    }
  }

  updateInventoryDisplay() {
    // Always recompute from latest committed storage state to avoid stale "today" UI.
    this.checkIfTodaySelected();
    const fulfillmentConstants = window.FulfillmentConstants;
    const locationKey = fulfillmentConstants && fulfillmentConstants.LOCATION_ID ? fulfillmentConstants.LOCATION_ID : 'selected_location_id';
    this.selectedLocationId = this.normalizeLocationId(localStorage.getItem(locationKey));

    if (!this.isFulfillmentReadyForReactiveUpdates()) {
      this.hideLoadingState();
      return;
    }
    
    if (!this.isTodaySelected) {
      this.clearInventoryData();
      return;
    }

    if (!this.selectedLocationId) {
      this.clearInventoryData();
      return;
    }

    // Show loading state
    this.showLoadingState();

      // Fetch inventory data and update cards
      this.fetchInventoryData().then(async data => {
        this.inventoryData = data;
        await this.updateProductCards();
        this.hideLoadingState();
      }).catch(error => {
      console.error('Failed to update inventory display:', error);
      this.hideLoadingState();
    });
  }

  showLoadingState() {
    const productCards = document.querySelectorAll('.product-card-wrapper, .recharge-card');
    productCards.forEach(card => {
      card.classList.add('inventory-loading');
    });
  }

  hideLoadingState() {
    const productCards = document.querySelectorAll('.product-card-wrapper, .recharge-card');
    productCards.forEach(card => {
      card.classList.remove('inventory-loading');
    });
  }

  setCartSnapshot(cart) {
    if (cart && Array.isArray(cart.items)) {
      this._cartSnapshot = cart;
      this._cartSnapshotAt = Date.now();
    }
  }

  async getCartSnapshot(maxAgeMs = 1500, options = {}) {
    const forceFresh = !!options.forceFresh;
    const now = Date.now();
    if (!forceFresh && this._cartSnapshot && (now - this._cartSnapshotAt) < maxAgeMs) {
      return this._cartSnapshot;
    }

    if (!forceFresh && window.cartWatcher && typeof window.cartWatcher.storedCart === 'function') {
      const watcherCart = window.cartWatcher.storedCart();
      if (watcherCart && Array.isArray(watcherCart.items)) {
        this.setCartSnapshot(watcherCart);
        return watcherCart;
      }
    }

    if (this._cartSnapshotPromise) {
      try {
        return await this._cartSnapshotPromise;
      } catch (error) {
        if (this._cartSnapshot && Array.isArray(this._cartSnapshot.items)) {
          return this._cartSnapshot;
        }
        throw error;
      }
    }

    this._cartSnapshotPromise = (async () => {
      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) {
        throw new Error(`/cart.js failed (${response.status})`);
      }
      const bodyText = await response.text();
      let cart = null;
      try {
        cart = JSON.parse(bodyText);
      } catch (error) {
        throw new Error('/cart.js returned non-JSON response');
      }
      if (!cart || !Array.isArray(cart.items)) {
        throw new Error('/cart.js returned invalid payload');
      }
      this.setCartSnapshot(cart);
      return cart;
    })();

    try {
      return await this._cartSnapshotPromise;
    } catch (error) {
      if (this._cartSnapshot && Array.isArray(this._cartSnapshot.items)) {
        return this._cartSnapshot;
      }
      throw error;
    } finally {
      this._cartSnapshotPromise = null;
    }
  }

  async updateProductCards(cartDataOverride = null, options = {}) {
    // Defensive sync: cart-update events can arrive after fulfillment changed.
    this.checkIfTodaySelected();
    const fulfillmentConstants = window.FulfillmentConstants;
    const locationKey = fulfillmentConstants && fulfillmentConstants.LOCATION_ID ? fulfillmentConstants.LOCATION_ID : 'selected_location_id';
    this.selectedLocationId = this.normalizeLocationId(localStorage.getItem(locationKey));
    if (!this.isTodaySelected || !this.selectedLocationId) {
      this.clearInventoryData();
      return;
    }

    if (!this.inventoryData) {
      this.inventoryData = await this.fetchInventoryData();
      if (!this.inventoryData) {
        return;
      }
    }

    const productCards = document.querySelectorAll('.product-card-wrapper, .recharge-card');
    const hasRechargeCards = productCards.length > 0 && Array.from(productCards).some(function (c) { return c.classList.contains('recharge-card'); });

    // Fetch cart data once for all cards to avoid multiple API calls
    let cartData = (cartDataOverride && Array.isArray(cartDataOverride.items)) ? cartDataOverride : null;
    if (!cartData && hasRechargeCards) {
      try {
        const raw = localStorage.getItem('rechargeSidebarCart');
        if (raw) {
          const sidebar = JSON.parse(raw);
          if (Array.isArray(sidebar)) {
            cartData = { items: sidebar.map(function (i) { return { variant_id: i.variantId, quantity: i.qty || 0 }; }) };
          }
        }
      } catch (e) {}
    }
    if (cartData) {
      this.setCartSnapshot(cartData);
    } else {
      try {
        cartData = await this.getCartSnapshot(
          options && options.forceFreshCart ? 0 : 1500,
          { forceFresh: !!(options && options.forceFreshCart) }
        );
      } catch (error) {
        console.error('InventoryChecker: Error fetching cart data:', error);
      }
    }

    for (const card of productCards) {
      const variantId = card.getAttribute('data-variant-id');
      const isBundle = card.getAttribute('data-is-bundle') === 'true';
      if (!variantId) continue;

      // Find inventory data for this variant
      let inventoryInfo = this.findVariantInventory(variantId);

      // If no direct variant inventory found and this is a bundle, calculate from components
      if (!inventoryInfo && isBundle) {
        const productId = card.getAttribute('data-product-id');
        if (productId) {
          inventoryInfo = this.getBundleInventory(productId);
        }
      }

      if (inventoryInfo) {
        // Add inventory data attributes
        card.setAttribute('data-inventory-available', inventoryInfo.available);
        card.setAttribute('data-inventory-committed', inventoryInfo.committed);
        card.setAttribute('data-inventory-incoming', inventoryInfo.incoming);

        // Check if sold out: available <= 0
        let isSoldOut = inventoryInfo.available <= 0;

        // For both bundles and regular products, check if cart has reached the maximum allowed quantity
        if (!isSoldOut && cartData) {
          const cartQuantity = (cartData.items || []).reduce((total, item) => {
            if (!item || item.variant_id == null) return total;
            return item.variant_id.toString() === variantId.toString()
              ? total + (Number(item.quantity) || 0)
              : total;
          }, 0);
          isSoldOut = cartQuantity >= inventoryInfo.available;
        }

        card.setAttribute('data-inventory-sold-out', isSoldOut);

        // Update visual state
        this.updateCardVisualState(card, isSoldOut, inventoryInfo.available);
      } else {
        // Special handling for bundles that might not have inventory data
        if (isBundle) {
          // For bundles with missing inventory data, treat as unavailable immediately
          card.setAttribute('data-inventory-available', 0);
          card.setAttribute('data-inventory-committed', 0);
          card.setAttribute('data-inventory-incoming', 0);
          card.setAttribute('data-inventory-sold-out', true);
          this.updateCardVisualState(card, true, 0);
        } else {
          // Clear inventory data if not found and not a bundle
          this.clearCardInventoryData(card);
        }
      }
    }
  }

  findVariantInventory(variantId) {
    if (!this.inventoryData || !this.inventoryData.products) {
      return null;
    }
    const normalizedVariantId = String(variantId);

    // Find the product that contains this specific variant
    for (const productId in this.inventoryData.products) {
      const product = this.inventoryData.products[productId];
      
      if (!product.variants) continue;
      
      // Check if this product contains the variant we're looking for
      if (product.variants && product.variants[normalizedVariantId]) {

        // Exception: Skip inventory checking for products in "pay-it-forward-collection"
        const isPayItForward = product.collections?.some((collection) =>
          collection.handle === 'pay-it-forward-collection' ||
          collection.handle === 'pay-it-forward'
        );
        if (isPayItForward) {
          return {
            available: 999, // Always show as available
            committed: 0,
            incoming: 0
          };
        }

        const toGoInventory = this.getToGoInventoryForProduct(product);
        if (toGoInventory) {
          return toGoInventory;
        }
      }
    }

    const contextProductId = this.findProductIdForVariant(normalizedVariantId);
    if (!contextProductId) {
      return null;
    }

    const contextProduct = this.inventoryData.products[contextProductId];
    if (!contextProduct) {
      return null;
    }

    const isPayItForwardContext = contextProduct.collections?.some((collection) =>
      collection.handle === 'pay-it-forward-collection' ||
      collection.handle === 'pay-it-forward'
    );
    if (isPayItForwardContext) {
      return {
        available: 999,
        committed: 0,
        incoming: 0
      };
    }

    const contextToGoInventory = this.getToGoInventoryForProduct(contextProduct);
    if (contextToGoInventory) {
      return contextToGoInventory;
    }

    if (contextProduct.bundleComponents && Array.isArray(contextProduct.bundleComponents) && contextProduct.bundleComponents.length > 0) {
      return this.getBundleInventory(contextProductId);
    }

    return null;
  }

  getToGoInventoryForProduct(product) {
    if (window.EtInventoryDomain && typeof window.EtInventoryDomain.getToGoInventoryForProduct === 'function') {
      return window.EtInventoryDomain.getToGoInventoryForProduct(product);
    }
    if (!product || !product.variants) {
      return null;
    }
    for (const variantIdKey in product.variants) {
      const variant = product.variants[variantIdKey];
      if (variant && variant.inventory) {
        return {
          available: variant.inventory.available || 0,
          committed: 0,
          incoming: variant.inventory.incoming || 0
        };
      }
    }
    return null;
  }

  // Find productId for a given variantId from inventory data or card attributes
  findProductIdForVariant(variantId) {
    if (!this.inventoryData || !this.inventoryData.products) {
      return null;
    }
    const normalizedVariantId = String(variantId);

    // First, try to find the product by checking if it has this variant
    for (const productId in this.inventoryData.products) {
      const product = this.inventoryData.products[productId];
      
      if (product.variants && product.variants[normalizedVariantId]) {
        return productId;
      }
    }
    
    // For bundles (which have empty variants), try to get productId from card's data-product-id
    const escapedId = (window.EtInventoryDomain && window.EtInventoryDomain.escapeForSelector)
      ? window.EtInventoryDomain.escapeForSelector(normalizedVariantId)
      : normalizedVariantId;
    const card = document.querySelector(
      `.product-card-wrapper[data-variant-id="${escapedId}"], .recharge-card[data-variant-id="${escapedId}"], [data-product-id][data-variant-id="${escapedId}"]`
    );
    if (card) {
      const cardProductId = this.normalizeProductId(card.getAttribute('data-product-id'));
      if (cardProductId && this.inventoryData.products[cardProductId]) {
        return cardProductId;
      }
    }
    
    return null;
  }

  // Check if a product is a bundle
  isBundleProduct(productId, variantId) {
    const escapedId = (window.EtInventoryDomain && window.EtInventoryDomain.escapeForSelector)
      ? window.EtInventoryDomain.escapeForSelector(String(variantId))
      : String(variantId);
    const bundleElements = document.querySelectorAll(`[data-variant-id="${escapedId}"][data-is-bundle="true"]`);
    return bundleElements.length > 0;
  }

  // Get inventory for a bundle based on its components
  getBundleInventory(productId) {
    if (!this.inventoryData || !this.inventoryData.products) {
      return null;
    }

    if (window.EtInventoryDomain && typeof window.EtInventoryDomain.getBundleInventory === 'function') {
      return window.EtInventoryDomain.getBundleInventory(this.inventoryData.products, productId);
    }

    const product = this.inventoryData.products[productId];
    if (!product) {
      return null;
    }

    if (!product.bundleComponents || !Array.isArray(product.bundleComponents) || product.bundleComponents.length === 0) {
      return {
        available: 0,
        committed: 0,
        incoming: 0
      };
    }

    const bundleComponents = product.bundleComponents;
    let minAvailable = Infinity;
    let totalIncoming = 0;

    for (const component of bundleComponents) {
      const componentProductId = component.product_id;
      const componentQuantity = component.quantity || 1;

      const componentInventory = this.findComponentInventory(componentProductId);
      if (componentInventory) {
        const availableForBundle = Math.floor(componentInventory.available / componentQuantity);
        minAvailable = Math.min(minAvailable, availableForBundle);
        totalIncoming += componentInventory.incoming || 0;
      } else {
        minAvailable = 0;
      }
    }

    if (minAvailable === Infinity) {
      minAvailable = 0;
    }

    return {
      available: minAvailable,
      committed: 0,
      incoming: totalIncoming
    };
  }

  findComponentInventory(productId) {
    if (!this.inventoryData || !this.inventoryData.products) {
      return null;
    }

    if (window.EtInventoryDomain && typeof window.EtInventoryDomain.findComponentInventory === 'function') {
      return window.EtInventoryDomain.findComponentInventory(this.inventoryData.products, productId);
    }

    const product = this.inventoryData.products[productId];
    if (!product || !product.variants) {
      return null;
    }

    for (const variantId in product.variants) {
      const variant = product.variants[variantId];
      if (variant && variant.inventory) {
        return {
          available: variant.inventory.available || 0,
          committed: 0,
          incoming: variant.inventory.incoming || 0
        };
      }
    }

    return null;
  }

  updateCardVisualState(card, isSoldOut, availableQuantity) {
    // Handle both product-card-wrapper and recharge-card structures
    const isRechargeCard = card.classList.contains('recharge-card');
    
    if (isRechargeCard) {
      // Handle recharge card structure
      const addButton = card.querySelector('.recharge-card__add-btn');
      
      if (isSoldOut) {
        // Disable add button
        if (addButton) {
          addButton.disabled = true;
          addButton.setAttribute('aria-disabled', 'true');
          addButton.style.opacity = '0.5';
        }
        
        // Show sold out label if not already present
        let soldOutLabel = card.querySelector('.sold-out-label');
        if (!soldOutLabel) {
          soldOutLabel = document.createElement('div');
          soldOutLabel.className = 'sold-out-label';
          soldOutLabel.textContent = 'Sold Out';
          soldOutLabel.style.cssText = `
            position: absolute;
            top: 10px;
            right: 10px;
            background: #a4cbc1;
            color: #00464b;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: bold;
            z-index: 10;
          `;
          card.style.position = 'relative';
          card.appendChild(soldOutLabel);
        }
      } else {
        // Enable add button
        this.resetButtonEnabledState(addButton);
        
        // Remove sold out label
        const soldOutLabel = card.querySelector('.sold-out-label');
        if (soldOutLabel) {
          soldOutLabel.remove();
        }
        
        // Show low stock warning if available quantity is low (less than 5)
        if (availableQuantity <= 5 && availableQuantity > 0) {
          // disable low stock warnings per Julie
          // keeping CSS in case this needs to be brought back
          this.hideLowStockWarning(card);
          // this.showLowStockWarning(card, availableQuantity);
        } else {
          this.hideLowStockWarning(card);
        }
      }
    } else {
      // Handle regular product card structure
      const addButton = card.querySelector('.add-button');
      const tagsContainer = card.querySelector('.card_tags');

      if (tagsContainer) {
        // Clean up legacy Sold out tags that were appended without a marker
        tagsContainer.querySelectorAll('.tag').forEach(tag => {
          if (!tag.hasAttribute('data-inventory-sold-out') && tag.textContent.trim().toLowerCase() === 'sold out') {
            tag.remove();
          }
        });
      }

      const soldOutTag = tagsContainer ? tagsContainer.querySelector('.tag[data-inventory-sold-out="true"]') : null;
      
      if (isSoldOut) {
        // Disable add button
        if (addButton) {
          addButton.disabled = true;
          addButton.setAttribute('aria-disabled', 'true');
          addButton.classList.add('disabled');
        }

        // Ensure Sold out tag exists and is marked so it isn't duplicated
        if (tagsContainer) {
          if (!soldOutTag) {
            const newSoldOutTag = document.createElement('div');
            newSoldOutTag.className = 'tag tag--sold-out';
            newSoldOutTag.setAttribute('data-inventory-sold-out', 'true');
            newSoldOutTag.textContent = 'Sold out';
            tagsContainer.appendChild(newSoldOutTag);
          } else {
            soldOutTag.textContent = 'Sold out';
          }
        }

        card.classList.add('sold-out');
      } else {
        // Enable add button
        this.resetButtonEnabledState(addButton);

        if (tagsContainer) {
          // Remove any Sold out tags created by the inventory checker
          tagsContainer.querySelectorAll('.tag[data-inventory-sold-out="true"]').forEach(tag => tag.remove());
          tagsContainer.querySelectorAll('.tag').forEach(tag => {
            if (!tag.hasAttribute('data-inventory-sold-out') && tag.textContent.trim().toLowerCase() === 'sold out') {
              tag.remove();
            }
          });
        }

        // Remove sold-out class from card
        card.classList.remove('sold-out');
        
        // Show low stock warning if available quantity is low (less than 5)
        if (availableQuantity <= 5 && availableQuantity > 0) {
          // disable low stock warnings per Julie
          // keeping CSS in case this needs to be brought back
          this.hideLowStockWarning(card);
          // this.showLowStockWarning(card, availableQuantity);
        } else {
          this.hideLowStockWarning(card);
        }
      }
    }
  }

  showLowStockWarning(card, quantity) {
    const isRechargeCard = card.classList.contains('recharge-card');
    
    if (isRechargeCard) {
      // Handle recharge card low stock warning
      let lowStockLabel = card.querySelector('.low-stock-label');
      if (!lowStockLabel) {
        lowStockLabel = document.createElement('div');
        lowStockLabel.className = 'low-stock-label';
        lowStockLabel.textContent = `Only ${quantity} left!`;
        lowStockLabel.style.cssText = `
          position: absolute;
          top: 10px;
          left: 10px;
          background: #ffa500;
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: bold;
          z-index: 2;
        `;
        card.style.position = 'relative';
        card.appendChild(lowStockLabel);
      }
    } else {
      // Handle regular product card low stock warning
      let lowStockTag = card.querySelector('.low-stock-warning');
      if (!lowStockTag) {
        const tagsContainer = card.querySelector('.card_tags');
        if (tagsContainer) {
          lowStockTag = document.createElement('div');
          lowStockTag.className = 'tag-light low-stock-warning';
          tagsContainer.appendChild(lowStockTag);
        }
      }
      
      if (lowStockTag) {
        lowStockTag.textContent = `Only ${quantity} left!`;
      }
    }
  }

  hideLowStockWarning(card) {
    const isRechargeCard = card.classList.contains('recharge-card');
    
    if (isRechargeCard) {
      const lowStockLabel = card.querySelector('.low-stock-label');
      if (lowStockLabel) {
        lowStockLabel.remove();
      }
    } else {
      const lowStockTag = card.querySelector('.low-stock-warning');
      if (lowStockTag) {
        lowStockTag.remove();
      }
    }
  }

  resetButtonEnabledState(button) {
    if (!button) return;
    button.disabled = false;
    button.removeAttribute('disabled');
    button.removeAttribute('aria-disabled');
    button.classList.remove('disabled');
    button.classList.remove('inventory-disabled');
    button.style.opacity = '';
    button.style.cursor = '';
    button.style.pointerEvents = '';
  }

  clearCardInventoryData(card) {
    card.removeAttribute('data-inventory-available');
    card.removeAttribute('data-inventory-committed');
    card.removeAttribute('data-inventory-incoming');
    card.removeAttribute('data-inventory-sold-out');
    card.classList.remove('sold-out');
    this.hideLowStockWarning(card);
    
    // Handle recharge card specific cleanup
    if (card.classList.contains('recharge-card')) {
      const soldOutLabel = card.querySelector('.sold-out-label');
      if (soldOutLabel) {
        soldOutLabel.remove();
      }
      const addButton = card.querySelector('.recharge-card__add-btn');
      this.resetButtonEnabledState(addButton);
    } else {
      const addButton = card.querySelector('.add-button');
      this.resetButtonEnabledState(addButton);

      const tagsContainer = card.querySelector('.card_tags');
      if (tagsContainer) {
        tagsContainer.querySelectorAll('.tag[data-inventory-sold-out="true"]').forEach((tag) => tag.remove());
        tagsContainer.querySelectorAll('.tag').forEach((tag) => {
          if (!tag.hasAttribute('data-inventory-sold-out') && tag.textContent.trim().toLowerCase() === 'sold out') {
            tag.remove();
          }
        });
      }
    }
  }

  clearInventoryData() {
    this.inventoryData = null;
    const productCards = document.querySelectorAll('.product-card-wrapper, .recharge-card');
    productCards.forEach(card => {
      this.clearCardInventoryData(card);
    });
  }

  addRefreshButton() {
    // Add a refresh button to the page if inventory is being checked
    if (this.isTodaySelected && this.selectedLocationId) {
      const existingButton = document.getElementById('inventory-refresh-btn');
      if (!existingButton) {
        const refreshButton = document.createElement('button');
        refreshButton.id = 'inventory-refresh-btn';
        refreshButton.className = 'inventory-refresh-button';
        refreshButton.innerHTML = '🔄 Refresh Inventory';
        refreshButton.onclick = () => this.refreshInventory();
        
        // Find a good place to insert the button (e.g., near the date selector)
        const dateSelector = document.getElementById('fulfillment-date-delivery');
        if (dateSelector && dateSelector.parentElement) {
          dateSelector.parentElement.appendChild(refreshButton);
        }
      }
    }
  }

  // Public method to manually trigger inventory check
  refreshInventory() {
    this.updateInventoryDisplay();
  }

  // Get current inventory status for a specific variant
  getVariantInventoryStatus(variantId) {
    if (!this.isTodaySelected || !this.selectedLocationId) {
      return null;
    }
    return this.findVariantInventory(variantId);
  }

  // Get current cart quantity for a variant
  async getCartQuantityForVariant(variantId) {
    try {
      const cart = await this.getCartSnapshot();

      const cartItem = cart.items.find(item => item.variant_id.toString() === variantId.toString());
      const quantity = cartItem ? cartItem.quantity : 0;
      
      return quantity;
    } catch (error) {
      console.error('InventoryChecker: Error getting cart quantity:', error);
      return 0;
    }
  }

  // Get current status
  getStatus() {
    return {
      isTodaySelected: this.isTodaySelected,
      selectedLocationId: this.selectedLocationId,
      isLoading: this.isLoading,
      hasInventoryData: !!this.inventoryData
    };
  }
}

function initializeInventoryChecker() {
  if (window.inventoryChecker && window.inventoryChecker instanceof InventoryChecker) {
    return window.inventoryChecker;
  }

  window.inventoryChecker = new InventoryChecker();
  return window.inventoryChecker;
}

// Listen for cart changes to update card visual states
subscribe(PUB_SUB_EVENTS.cartUpdate, async function(event) {
  if (window.inventoryChecker) {
    if (!window.inventoryChecker.isFulfillmentReadyForReactiveUpdates()) {
      return;
    }

    const maybeCart = event && event.cartData && Array.isArray(event.cartData.items) ? event.cartData : null;
    const needsFreshCart =
      !maybeCart &&
      !!(event && (event.source === 'product-form' || event.productVariantId));

    if (needsFreshCart) {
      window.inventoryChecker._cartSnapshot = null;
      window.inventoryChecker._cartSnapshotAt = 0;
    }

    if (window.inventoryChecker._updateCardsTimer) {
      clearTimeout(window.inventoryChecker._updateCardsTimer);
    }

    const delay = needsFreshCart ? 280 : (event && event.source === 'cart-items' ? 320 : 180);
    window.inventoryChecker._updateCardsTimer = setTimeout(async () => {
      await window.inventoryChecker.updateProductCards(maybeCart, { forceFreshCart: needsFreshCart });
    }, delay);
  }
});

// Listen for fulfillment date changes
window.addEventListener('fulfillmentDateChanged', function() {
  if (window.inventoryChecker) {
    if (!window.inventoryChecker.isFulfillmentReadyForReactiveUpdates()) {
      window.inventoryChecker.hideLoadingState();
      return;
    }
    // Re-fetch inventory data and update cards
    window.inventoryChecker.updateInventoryDisplay();
  }
});

if (document.readyState === 'loading') {
  const onReady = () => {
    document.removeEventListener('DOMContentLoaded', onReady);
    initializeInventoryChecker();
  };
  document.addEventListener('DOMContentLoaded', onReady);
} else {
  initializeInventoryChecker();
}
